import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { createOrUpdateApplication } from '../lib/api/applications';
import {
  getAttendeeProfile,
  createOrUpdateAttendeeProfile,
  createOrUpdateEmergencyContact,
  createOrUpdateChurchInfo,
  createOrUpdateFaithInfo,
  getProfileCustomFields,
  saveProfileCustomFields,
} from '../lib/api/profiles';
import { getEventCustomFields } from '../lib/api/events';
import type { EventCustomField, EventSignupOption, SignupOptionMode } from '../types/database';
import DynamicFormField, { validateCustomField, getDefaultValue } from './DynamicFormField';
import ApplicationConfirmation, { type CoordinatorContact } from './ApplicationConfirmation';
import ChurchNameInput from './ChurchNameInput';
import './MissionApplicationForm.css';
import { captureError } from '../lib/capture-error';
import { formatSignupOptionSchedule } from '../lib/dates';
import { phoneToProfileFields, resolveProfilePhone } from '../lib/event-application';
import { buildEventApplyPath, formatSignupPrice, hasSignupPrice } from '../lib/signup-options';

const PENDING_APPLICATION_KEY_PREFIX = 'pending_application_';
const buildPendingApplicationKey = (email: string, eventId: string) =>
  `${PENDING_APPLICATION_KEY_PREFIX}${encodeURIComponent(eventId)}_${encodeURIComponent(email)}`;
const buildLegacyPendingApplicationKey = (email: string) =>
  `${PENDING_APPLICATION_KEY_PREFIX}${encodeURIComponent(email)}`;

/** Call an edge function for guest users (no auth required) */
async function callEdgeFunction(functionName: string, body: Record<string, any>): Promise<{ data: any; error: any }> {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) {
    return { data: null, error: new Error('Supabase not configured') };
  }
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const err = Object.assign(
        new Error(data?.error || `Request failed with status ${response.status}`),
        { status: response.status, responseBody: data, functionName }
      );
      return { data: null, error: err };
    }
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

/** Payload we store when email confirmation is required so we can save after the user confirms */
interface PendingApplicationPayload {
  formData: ApplicationData;
  eventId: string;
  eventSlug?: string;
  eventCustomFields: EventCustomField[];
  customFieldResponses: Record<string, any>;
  selectedSignupOptions?: string[];
  action: 'draft' | 'submit';
}

async function createPendingApplication(
  email: string,
  payload: PendingApplicationPayload
): Promise<{ token: string | null; error: any }> {
  const expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString();
  const { data, error } = await callEdgeFunction('pending', {
    email,
    payload,
    expires_at: expiresAt,
  });
  if (error || !data?.token) {
    return { token: null, error: error || new Error(data?.error || 'Failed to create pending application') };
  }
  return { token: data.token as string, error: null };
}

interface ApplicationData {
  // Personal Information
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: {
    month: string;
    day: string;
    year: string;
  };
  tshirtSize: string;

  // Address
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  
  // Contact Information
  phone: string;
  email: string;
  
  // Emergency Contact
  emergencyContact: {
    firstName: string;
    lastName: string;
    phone: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
  };
  
  // Church Information
  church: {
    name: string;
    pastorFirstName: string;
    pastorLastName: string;
    phone: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
  };

  // Faith Information
  faith: {
    bornAgain: string;
    believerLength: string;
    testimony: string;
    evangelismExperience: string;
    currentlyPracticing: string;
    missionTripExperience: string;
    previousMissionTrips: string;
  };
  
  // Opt-in
  optedIn: boolean;
}

/** Backward compat for pending payloads saved before phone field consolidation. */
type LegacyApplicationData = ApplicationData & {
  cellPhone?: string;
  homePhone?: string;
  workPhone?: string;
};

function resolveFormPhone(formData: LegacyApplicationData): string {
  return (
    formData.phone?.trim() ||
    formData.cellPhone?.trim() ||
    formData.homePhone?.trim() ||
    formData.workPhone?.trim() ||
    ''
  );
}

function formatDateOfBirth(dob: ApplicationData['dateOfBirth']): string {
  if (!dob.year || !dob.month || !dob.day) return '';
  return `${dob.year}-${dob.month.padStart(2, '0')}-${dob.day.padStart(2, '0')}`;
}

interface MissionApplicationFormProps {
  eventId: string;
  eventSlug?: string;
  eventTitle?: string;
  /** Coordinator contact for post-submission "Questions?" section (email required; phone optional). */
  coordinatorContact?: CoordinatorContact | null;
  /** Available signup options for this event. */
  signupOptions?: EventSignupOption[];
  /** Selection mode: 'single' (radio) or 'multi' (checkbox). */
  signupOptionMode?: SignupOptionMode;
  /** Initially selected signup option IDs (from URL params). */
  initialSelectedOptions?: string[];
}

const MissionApplicationForm: React.FC<MissionApplicationFormProps> = ({
  eventId,
  eventSlug,
  eventTitle = "OBP Italy 2026",
  coordinatorContact = null,
  signupOptions = [],
  signupOptionMode = 'single',
  initialSelectedOptions = [],
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showAlreadyApplied, setShowAlreadyApplied] = useState(false);
  const [isNewAccount, setIsNewAccount] = useState(false);

  // Custom fields state
  const [eventCustomFields, setEventCustomFields] = useState<EventCustomField[]>([]);
  const [customFieldResponses, setCustomFieldResponses] = useState<Record<string, any>>({});
  const [savedCustomFieldKeys, setSavedCustomFieldKeys] = useState<Set<string>>(new Set()); // Track which fields are pre-filled from profile

  // Signup options state (managed internally, initialized from props)
  const [selectedSignupOptions, setSelectedSignupOptions] = useState<string[]>(initialSelectedOptions);

  const signInRedirectPath = useMemo(() => {
    if (selectedSignupOptions.length === 1) {
      return buildEventApplyPath(eventSlug || eventId, selectedSignupOptions[0]);
    }
    return buildEventApplyPath(eventSlug || eventId);
  }, [eventSlug, eventId, selectedSignupOptions]);

  const [formData, setFormData] = useState<ApplicationData>({
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: { month: '', day: '', year: '' },
    tshirtSize: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      zip: '',
      country: 'United States'
    },
    phone: '',
    email: user?.email || '',
    emergencyContact: {
      firstName: '',
      lastName: '',
      phone: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States'
      }
    },
    church: {
      name: '',
      pastorFirstName: '',
      pastorLastName: '',
      phone: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States'
      }
    },
    faith: {
      bornAgain: '',
      believerLength: '',
      testimony: '',
      evangelismExperience: '',
      currentlyPracticing: '',
      missionTripExperience: '',
      previousMissionTrips: ''
    },
    optedIn: false
  });

  const totalSteps = 6;

  // Load existing application data
  useEffect(() => {
    const loadApplicationData = async () => {
      if (!user) return;

      try {
        const profile = await getAttendeeProfile(user.id);
        if (profile) {
          // Map profile data to form data
          setFormData(prev => ({
            ...prev,
            // Personal Info
            firstName: profile.first_name || '',
            lastName: profile.last_name || '',
            gender: profile.gender || '',
            dateOfBirth: {
              month: profile.date_of_birth ? (new Date(profile.date_of_birth).getMonth() + 1).toString() : '',
              day: profile.date_of_birth ? new Date(profile.date_of_birth).getDate().toString() : '',
              year: profile.date_of_birth ? new Date(profile.date_of_birth).getFullYear().toString() : ''
            },
            tshirtSize: profile.tshirt_size || '',
            address: {
              line1: profile.address_line1 || '',
              line2: profile.address_line2 || '',
              city: profile.city || '',
              state: profile.state || '',
              zip: profile.zip || '',
              country: profile.country || 'United States'
            },
            phone: resolveProfilePhone(profile),
            email: profile.email || user.email || '',

            // Emergency Contact (pre-fill from profile)
            emergencyContact: profile.emergency_contact ? {
              firstName: profile.emergency_contact.first_name || '',
              lastName: profile.emergency_contact.last_name || '',
              phone: profile.emergency_contact.phone_number || '',
              address: {
                line1: profile.emergency_contact.address_line1 || '',
                line2: profile.emergency_contact.address_line2 || '',
                city: profile.emergency_contact.city || '',
                state: profile.emergency_contact.state || '',
                zip: profile.emergency_contact.zip || '',
                country: profile.emergency_contact.country || 'United States'
              }
            } : prev.emergencyContact,

            // Church Info (pre-fill from profile)
            church: profile.church_info ? {
              name: profile.church_info.church_name || '',
              pastorFirstName: profile.church_info.pastor_first_name || '',
              pastorLastName: profile.church_info.pastor_last_name || '',
              phone: profile.church_info.phone_number || '',
              address: {
                line1: profile.church_info.address_line1 || '',
                line2: profile.church_info.address_line2 || '',
                city: profile.church_info.city || '',
                state: profile.church_info.state || '',
                zip: profile.church_info.zip || '',
                country: profile.church_info.country || 'United States'
              }
            } : prev.church,

            // Faith Info (pre-fill from profile)
            faith: profile.faith_info ? {
              bornAgain: profile.faith_info.is_born_again_believer ? 'Yes' : (profile.faith_info.is_born_again_believer === false ? 'No' : ''),
              believerLength: profile.faith_info.believer_length || '',
              testimony: profile.faith_info.personal_testimony || '',
              evangelismExperience: profile.faith_info.evangelism_experience || '',
              currentlyPracticing: profile.faith_info.currently_practicing_evangelism ? 'Yes' : (profile.faith_info.currently_practicing_evangelism === false ? 'No' : ''),
              missionTripExperience: profile.faith_info.mission_trip_experience || '',
              previousMissionTrips: profile.faith_info.has_been_on_mission_trip ? 'Yes' : (profile.faith_info.has_been_on_mission_trip === false ? 'No' : '')
            } : prev.faith
          }));
        }
      } catch (error) {
        captureError(error, 'Error loading profile data');
      }
    };

    loadApplicationData();
  }, [user]);

  // Load event custom fields and user's saved responses
  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        // Load custom fields for this event
        const fields = await getEventCustomFields(eventId);
        setEventCustomFields(fields);

        // Initialize custom field responses with defaults
        const defaultResponses: Record<string, any> = {};
        fields.forEach((field) => {
          defaultResponses[field.field_key] = getDefaultValue(field.field_type);
        });

        // If user is logged in and has a profile, load saved custom field responses
        if (user) {
          const profile = await getAttendeeProfile(user.id);
          if (profile?.id) {
            const savedFields = await getProfileCustomFields(profile.id);
            const savedKeys = new Set<string>();

            // Map saved responses to field keys
            savedFields.forEach((saved) => {
              if (defaultResponses.hasOwnProperty(saved.field_key)) {
                defaultResponses[saved.field_key] = saved.field_value;
                savedKeys.add(saved.field_key);
              }
            });

            setSavedCustomFieldKeys(savedKeys);
          }
        }

        setCustomFieldResponses(defaultResponses);
      } catch (error) {
        captureError(error, 'Error loading custom fields');
      }
    };

    loadCustomFields();
  }, [eventId, user]);

  /**
   * Saves profile, related records, and application to the database.
   * Uses the current Supabase session (RLS), so only call when the user has a session.
   * Returns the application id if created/updated, or null if no profile was created.
   */
  const saveApplicationDataWithPayload = useCallback(async (
    userId: string,
    action: 'draft' | 'submit',
    payload: PendingApplicationPayload
  ): Promise<string | null> => {
    const { formData: fd, eventId: eid, eventCustomFields: fields, customFieldResponses: responses } = payload;
    const phoneFields = phoneToProfileFields(resolveFormPhone(fd));
    const profile = await createOrUpdateAttendeeProfile(userId, {
      first_name: fd.firstName,
      last_name: fd.lastName,
      gender: fd.gender ? (fd.gender as 'Male' | 'Female') : null,
      date_of_birth: fd.dateOfBirth.year
        ? new Date(parseInt(fd.dateOfBirth.year), parseInt(fd.dateOfBirth.month) - 1, parseInt(fd.dateOfBirth.day)).toISOString()
        : '',
      tshirt_size: fd.tshirtSize || null,
      address_line1: fd.address.line1 || null,
      address_line2: fd.address.line2 || null,
      city: fd.address.city || null,
      state: fd.address.state || null,
      zip: fd.address.zip || null,
      country: fd.address.country || null,
      ...phoneFields,
      email: fd.email,
    });

    if (!profile.id) return null;

    await createOrUpdateEmergencyContact(profile.id, {
      first_name: fd.emergencyContact.firstName,
      last_name: fd.emergencyContact.lastName,
      phone_number: fd.emergencyContact.phone,
      address_line1: fd.emergencyContact.address.line1,
      address_line2: fd.emergencyContact.address.line2,
      city: fd.emergencyContact.address.city,
      state: fd.emergencyContact.address.state,
      zip: fd.emergencyContact.address.zip,
      country: fd.emergencyContact.address.country,
    });

    await createOrUpdateChurchInfo(profile.id, {
      church_name: fd.church.name,
      pastor_first_name: fd.church.pastorFirstName,
      pastor_last_name: fd.church.pastorLastName,
      phone_number: fd.church.phone,
      address_line1: fd.church.address.line1,
      address_line2: fd.church.address.line2,
      city: fd.church.address.city,
      state: fd.church.address.state,
      zip: fd.church.address.zip,
      country: fd.church.address.country,
    });

    await createOrUpdateFaithInfo(profile.id, {
      is_born_again_believer: fd.faith.bornAgain === 'Yes',
      believer_length: fd.faith.believerLength,
      personal_testimony: fd.faith.testimony,
      evangelism_experience: fd.faith.evangelismExperience,
      currently_practicing_evangelism: fd.faith.currentlyPracticing === 'Yes',
      mission_trip_experience: fd.faith.missionTripExperience,
      has_been_on_mission_trip: fd.faith.previousMissionTrips === 'Yes',
    });

    const fieldsToSaveToProfile = fields
      .filter((field) => field.save_to_profile && responses[field.field_key] !== undefined)
      .map((field) => ({ fieldKey: field.field_key, fieldValue: responses[field.field_key] }));

    if (fieldsToSaveToProfile.length > 0) {
      await saveProfileCustomFields(profile.id, fieldsToSaveToProfile);
    }

    const application = await createOrUpdateApplication(
      eid,
      profile.id,
      { ...responses, opted_in: fd.optedIn },
      action === 'submit' ? 'submitted' : 'draft',
      payload.selectedSignupOptions || null
    );
    return application.id;
  }, []);

  // When user lands with a session after email confirmation, fetch pending application
  // from server (keyed by token in localStorage; payload stays server-side).
  useEffect(() => {
    if (!user?.email || !user?.id || !eventId) return;
    const eventKey = buildPendingApplicationKey(user.email, eventId);
    const legacyKey = buildLegacyPendingApplicationKey(user.email);
    const eventRaw = localStorage.getItem(eventKey);
    const legacyRaw = eventRaw ? null : localStorage.getItem(legacyKey);
    const raw = eventRaw ?? legacyRaw;
    if (!raw) return;
    const key = eventRaw ? eventKey : legacyKey;

    (async () => {
      try {
        let payload: PendingApplicationPayload | null = null;

        // Backward compat: if old format (full JSON payload), use once then clear
        if (raw.trim().startsWith('{')) {
          try {
            payload = JSON.parse(raw) as PendingApplicationPayload;
            if (!payload?.formData || !payload.eventId) payload = null;
          } catch {
            payload = null;
          }
          if (payload && payload.eventId !== eventId) {
            const targetKey = buildPendingApplicationKey(user.email!, payload.eventId);
            if (targetKey !== key && !localStorage.getItem(targetKey)) {
              localStorage.setItem(targetKey, raw);
            }
            if (targetKey !== key) {
              localStorage.removeItem(key);
            }
            return;
          }
          if (payload) localStorage.removeItem(key);
        }

        // New format: raw value is the pending_applications token only (payload via Edge Function)
        if (!payload) {
          const token = raw.trim();
          const { data, error } = await supabase.functions.invoke('pending', {
            method: 'POST',
            body: { token },
          });
          if (error || data?.error || !data?.payload) {
            localStorage.removeItem(key);
            return;
          }
          payload = data.payload as PendingApplicationPayload;
          if (!payload?.eventId) {
            localStorage.removeItem(key);
            return;
          }
          if (payload.eventId !== eventId) {
            const targetKey = buildPendingApplicationKey(user.email!, payload.eventId);
            if (targetKey !== key && !localStorage.getItem(targetKey)) {
              localStorage.setItem(targetKey, token);
            }
            if (targetKey !== key) {
              localStorage.removeItem(key);
            }
            return;
          }
          await supabase
            .from('pending_applications')
            .delete()
            .eq('token', token)
            .eq('email', user.email);
          localStorage.removeItem(key);
        }

        if (!payload) return;

        const appId = await saveApplicationDataWithPayload(user.id, payload.action, payload);
        setSubmittedApplicationId(appId);
        setIsNewAccount(true);
        setIsSubmitted(true);
} catch (err) {
          captureError(err, 'Error saving pending application after confirmation');
          localStorage.removeItem(key);
        }
    })();
  }, [user?.id, user?.email, eventId, saveApplicationDataWithPayload]);

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof ApplicationData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle custom field changes
  const handleCustomFieldChange = (fieldKey: string, value: any) => {
    setCustomFieldResponses((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));

    // Clear error when user changes value
    if (errors[`customField.${fieldKey}`]) {
      setErrors((prev) => ({
        ...prev,
        [`customField.${fieldKey}`]: '',
      }));
    }
  };

  // Returns errors for a single step (no state update). Used to accumulate errors across all steps in handleSubmit.
  const getErrorsForStep = (step: number): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1: // Personal Information
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!resolveFormPhone(formData).trim()) newErrors.phone = 'Phone number is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.dateOfBirth.month) newErrors['dateOfBirth.month'] = 'Birth month is required';
        if (!formData.dateOfBirth.day) newErrors['dateOfBirth.day'] = 'Birth day is required';
        if (!formData.dateOfBirth.year) newErrors['dateOfBirth.year'] = 'Birth year is required';
        if (!formData.tshirtSize) newErrors.tshirtSize = 'T-shirt size is required';
        break;

      case 2: // Address
        if (!formData.address.line1.trim()) newErrors['address.line1'] = 'Address line 1 is required';
        if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required';
        if (!formData.address.state.trim()) newErrors['address.state'] = 'State is required';
        if (!formData.address.zip.trim()) newErrors['address.zip'] = 'ZIP code is required';
        break;

      case 3: // Emergency Contact
        if (!formData.emergencyContact.firstName.trim()) newErrors['emergencyContact.firstName'] = 'Emergency contact first name is required';
        if (!formData.emergencyContact.lastName.trim()) newErrors['emergencyContact.lastName'] = 'Emergency contact last name is required';
        if (!formData.emergencyContact.phone.trim()) newErrors['emergencyContact.phone'] = 'Emergency contact phone is required';
        break;

      case 4: // Church Information
        if (!formData.church.name.trim()) newErrors['church.name'] = 'Church name is required';
        if (!formData.church.pastorFirstName.trim()) newErrors['church.pastorFirstName'] = 'Pastor first name is required';
        if (!formData.church.pastorLastName.trim()) newErrors['church.pastorLastName'] = 'Pastor last name is required';
        if (!formData.church.phone.trim()) newErrors['church.phone'] = 'Church phone is required';
        break;

      case 5: // Faith Information
        if (!formData.faith.bornAgain) newErrors['faith.bornAgain'] = 'Please answer if you are a born again believer';
        if (!formData.faith.believerLength.trim()) newErrors['faith.believerLength'] = 'Please specify how long you have been a believer';
        if (!formData.faith.testimony.trim()) newErrors['faith.testimony'] = 'Personal testimony is required';
        break;

      case 6: // Event Custom Fields
        eventCustomFields.forEach((field) => {
          const validation = validateCustomField(field, customFieldResponses[field.field_key]);
          if (!validation.valid && validation.error) {
            newErrors[`customField.${field.field_key}`] = validation.error;
          }
        });
        break;
    }

    return newErrors;
  };

  const validateStep = (step: number): boolean => {
    const stepErrors = getErrorsForStep(step);
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Handle signup option toggle
  const handleSignupOptionToggle = (optionId: string) => {
    if (signupOptionMode === 'single') {
      setSelectedSignupOptions([optionId]);
    } else {
      setSelectedSignupOptions((prev) =>
        prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId]
      );
    }
    // Clear signup option error when selection changes
    if (errors.signupOptions) {
      setErrors((prev) => {
        const { signupOptions: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Helper function to create account and save application
  const createAccountAndSave = async (action: 'draft' | 'submit') => {
    if (!formData.email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Email is required to save your application' }));
      setCurrentStep(1);
      return;
    }

    setIsSubmitting(true);
    try {
      // Generate a cryptographically strong random password
      // User will set their own password via account setup page
      const randomBytes = new Uint8Array(32);
      crypto.getRandomValues(randomBytes);
      const tempPassword = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');

      // Build the redirect URL - prefer eventSlug for cleaner URLs, fallback to eventId
      const eventPath = eventSlug?.trim() || eventId;
      const redirectUrl = `${window.location.origin}/events/${encodeURIComponent(eventPath)}/apply`;

      // Try to sign up with temporary password
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: tempPassword,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
          }
        }
      });

      if (error) {
        // Check for rate limit error
        if (error.message.includes('rate limit') || (error as any).code === 'over_email_send_rate_limit') {
          throw new Error('Too many attempts. Please wait a few minutes before trying again, or check your email for a confirmation link.');
        }
        // Check if user already exists (server may return explicit error)
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          setShowLoginPrompt(true);
          setIsSubmitting(false);
          return;
        }
        throw error;
      }

      // With email confirmation enabled, signUp with existing email returns user but no session and empty identities
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        setShowLoginPrompt(true);
        setIsSubmitting(false);
        return;
      }

      if (data.user) {
        // With email confirmation enabled, signUp returns user but no session; RLS would block
        // profile/application inserts. Only save when we have a session (e.g. confirmation disabled).
        const buildPendingPayload = (): PendingApplicationPayload => ({
          formData,
          eventId,
          eventSlug,
          eventCustomFields,
          customFieldResponses,
          selectedSignupOptions,
          action,
        });

        if (data.session) {
          await saveApplicationData(data.user.id, action);
        } else {
          // No session - submit application anonymously via edge function
          // The application will be created without a user_id and can be claimed later
          const dateOfBirthStr = formatDateOfBirth(formData.dateOfBirth);
          const phoneFields = phoneToProfileFields(resolveFormPhone(formData));

          const { data: submitData, error: submitError } = await callEdgeFunction('submit-application', {
            eventId,
            action,
            profile: {
              first_name: formData.firstName,
              last_name: formData.lastName,
              email: formData.email,
              ...phoneFields,
              date_of_birth: dateOfBirthStr,
              gender: formData.gender || undefined,
              address_line1: formData.address.line1 || undefined,
              address_line2: formData.address.line2 || undefined,
              city: formData.address.city || undefined,
              state: formData.address.state || undefined,
              zip: formData.address.zip || undefined,
              country: formData.address.country || undefined,
              tshirt_size: formData.tshirtSize || undefined,
            },
            emergency_contact: formData.emergencyContact?.firstName ? {
              first_name: formData.emergencyContact.firstName,
              last_name: formData.emergencyContact.lastName,
              phone_number: formData.emergencyContact.phone,
              address_line1: formData.emergencyContact.address.line1,
              address_line2: formData.emergencyContact.address.line2,
              city: formData.emergencyContact.address.city,
              state: formData.emergencyContact.address.state,
              zip: formData.emergencyContact.address.zip,
              country: formData.emergencyContact.address.country,
            } : undefined,
            church_info: formData.church?.name ? {
              church_name: formData.church.name,
              pastor_first_name: formData.church.pastorFirstName,
              pastor_last_name: formData.church.pastorLastName,
              phone_number: formData.church.phone,
              address_line1: formData.church.address.line1,
              address_line2: formData.church.address.line2,
              city: formData.church.address.city,
              state: formData.church.address.state,
              zip: formData.church.address.zip,
              country: formData.church.address.country,
            } : undefined,
            faith_info: formData.faith ? {
              is_born_again_believer: formData.faith.bornAgain === 'Yes',
              believer_length: formData.faith.believerLength,
              personal_testimony: formData.faith.testimony,
              evangelism_experience: formData.faith.evangelismExperience,
              currently_practicing_evangelism: formData.faith.currentlyPracticing === 'Yes',
              mission_trip_experience: formData.faith.missionTripExperience,
              has_been_on_mission_trip: formData.faith.previousMissionTrips === 'Yes',
            } : undefined,
            custom_field_responses: customFieldResponses,
            selected_signup_options: selectedSignupOptions,
          });

          if (submitError || submitData?.error) {
            const errorCode = submitError?.message || submitData?.error || '';
            if (errorCode === 'already_registered') {
              setShowLoginPrompt(true);
              setIsSubmitting(false);
              return;
            }
            if (errorCode === 'already_applied') {
              setShowAlreadyApplied(true);
              setIsSubmitting(false);
              return;
            }
            // Defensive fallback: if edge auth/config rejects anonymous submission,
            // store payload server-side and complete after email confirmation login.
            const status = (submitError as { status?: number } | undefined)?.status;
            if (status === 401 || status === 403 || errorCode.includes('401') || errorCode.toLowerCase().includes('unauthorized')) {
              const pendingPayload = buildPendingPayload();
              const { token, error: pendingError } = await createPendingApplication(formData.email, pendingPayload);
              if (!pendingError && token) {
                const pendingKey = buildPendingApplicationKey(formData.email, eventId);
                localStorage.setItem(pendingKey, token);
                setIsNewAccount(true);
                if (action === 'submit') {
                  setIsSubmitted(true);
                } else {
                  showToast('Draft saved. Please confirm your email to continue.', 'success');
                }
                return;
              }
            }
            throw submitError || new Error(submitData?.error || 'Failed to save application');
          }

          setSubmittedApplicationId(submitData.applicationId);
        }
        setIsNewAccount(true);
        if (action === 'submit') {
          setIsSubmitted(true);
        } else {
          showToast('Draft saved successfully!', 'success');
        }
      }
    } catch (error: any) {
      captureError(error, 'Error creating account');
      // Provide user-friendly error messages
      let friendlyMessage = 'Failed to save application. Please try again.';
      const msg = error?.message?.toLowerCase() || '';
      if (msg.includes('jwt') || msg.includes('unauthorized') || msg.includes('401') || msg.includes('non-2xx')) {
        friendlyMessage = 'There was a problem saving your application. Please try again, or contact us if the problem persists.';
      } else if (msg.includes('network') || msg.includes('fetch')) {
        friendlyMessage = 'Unable to connect. Please check your internet connection and try again.';
      } else if (msg.includes('rate limit')) {
        friendlyMessage = 'Too many attempts. Please wait a few minutes before trying again.';
      } else if (error.message && !msg.includes('error') && error.message.length < 100) {
        // Use the original message if it seems user-friendly
        friendlyMessage = error.message;
      }
      setSaveError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveApplicationData = async (userId: string, action: 'draft' | 'submit') => {
    const payload: PendingApplicationPayload = {
      formData,
      eventId,
      eventSlug,
      eventCustomFields,
      customFieldResponses,
      selectedSignupOptions,
      action,
    };
    const appId = await saveApplicationDataWithPayload(userId, action, payload);
    setSubmittedApplicationId(appId);
  };

  /** Persist application: guest path creates account then saves; logged-in path saves via saveApplicationData. */
  const performSave = async (action: 'draft' | 'submit') => {
    setSaveError(null);
    if (!user) {
      await createAccountAndSave(action);
      return;
    }
    setIsSubmitting(true);
    try {
      await saveApplicationData(user.id, action);
      if (action === 'submit') {
        setIsSubmitted(true);
      } else {
        showToast('Draft saved successfully!', 'success');
      }
    } catch (error: any) {
      const isSubmit = action === 'submit';
      captureError(error, isSubmit ? 'Error submitting application' : 'Error saving draft');
      const errorMessage = error?.message || (isSubmit ? 'Error submitting application. Please try again.' : 'Error saving draft. Please try again.');
      setSaveError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => performSave('draft');

  const handleSubmit = async () => {
    const allErrors: Record<string, string> = {};

    // Validate signup options if they exist
    if (signupOptions.length > 0 && selectedSignupOptions.length === 0) {
      allErrors.signupOptions = 'Please select a registration option';
    }

    for (let step = 1; step <= totalSteps; step++) {
      const stepErrors = getErrorsForStep(step);
      Object.assign(allErrors, stepErrors);
    }
    setErrors(allErrors);

    if (Object.keys(allErrors).length > 0) {
      showToast('Please complete all required fields before submitting.', 'error');
      return;
    }

    await performSave('submit');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3 className="step-title">Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
              
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>

              <div className="form-group">
                <label>Gender *</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className={errors.gender ? 'error' : ''}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender && <span className="error-text">{errors.gender}</span>}
              </div>
              
              <div className="form-group">
                <label>Date of Birth *</label>
                <div className="date-inputs">
                  <select
                    value={formData.dateOfBirth.month}
                    onChange={(e) => handleInputChange('dateOfBirth.month', e.target.value)}
                    className={errors['dateOfBirth.month'] ? 'error' : ''}
                  >
                    <option value="">Month</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Day"
                    value={formData.dateOfBirth.day}
                    onChange={(e) => handleInputChange('dateOfBirth.day', e.target.value)}
                    className={errors['dateOfBirth.day'] ? 'error' : ''}
                    min="1"
                    max="31"
                  />
                  <input
                    type="number"
                    placeholder="Year"
                    value={formData.dateOfBirth.year}
                    onChange={(e) => handleInputChange('dateOfBirth.year', e.target.value)}
                    className={errors['dateOfBirth.year'] ? 'error' : ''}
                    min="1900"
                    max="2010"
                  />
                </div>
                {(errors['dateOfBirth.month'] || errors['dateOfBirth.day'] || errors['dateOfBirth.year']) && (
                  <span className="error-text">Date of birth is required</span>
                )}
              </div>
              
              <div className="form-group">
                <label>T-Shirt Size *</label>
                <select
                  value={formData.tshirtSize}
                  onChange={(e) => handleInputChange('tshirtSize', e.target.value)}
                  className={errors.tshirtSize ? 'error' : ''}
                >
                  <option value="">Select Size</option>
                  <option value="Small">Small</option>
                  <option value="Medium">Medium</option>
                  <option value="Large">Large</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                  <option value="XXXL">XXXL</option>
                </select>
                {errors.tshirtSize && <span className="error-text">{errors.tshirtSize}</span>}
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="step-content">
            <h3 className="step-title">Address Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address Line 1 *</label>
                <input
                  type="text"
                  value={formData.address.line1}
                  onChange={(e) => handleInputChange('address.line1', e.target.value)}
                  className={errors['address.line1'] ? 'error' : ''}
                />
                {errors['address.line1'] && <span className="error-text">{errors['address.line1']}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={formData.address.line2}
                  onChange={(e) => handleInputChange('address.line2', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  className={errors['address.city'] ? 'error' : ''}
                />
                {errors['address.city'] && <span className="error-text">{errors['address.city']}</span>}
              </div>
              
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  className={errors['address.state'] ? 'error' : ''}
                />
                {errors['address.state'] && <span className="error-text">{errors['address.state']}</span>}
              </div>
              
              <div className="form-group">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  value={formData.address.zip}
                  onChange={(e) => handleInputChange('address.zip', e.target.value)}
                  className={errors['address.zip'] ? 'error' : ''}
                />
                {errors['address.zip'] && <span className="error-text">{errors['address.zip']}</span>}
              </div>
              
              <div className="form-group">
                <label>Country *</label>
                <select
                  value={formData.address.country}
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="step-content">
            <h3 className="step-title">Emergency Contact</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.emergencyContact.firstName}
                  onChange={(e) => handleInputChange('emergencyContact.firstName', e.target.value)}
                  className={errors['emergencyContact.firstName'] ? 'error' : ''}
                />
                {errors['emergencyContact.firstName'] && <span className="error-text">{errors['emergencyContact.firstName']}</span>}
              </div>
              
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.emergencyContact.lastName}
                  onChange={(e) => handleInputChange('emergencyContact.lastName', e.target.value)}
                  className={errors['emergencyContact.lastName'] ? 'error' : ''}
                />
                {errors['emergencyContact.lastName'] && <span className="error-text">{errors['emergencyContact.lastName']}</span>}
              </div>
              
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.emergencyContact.phone}
                  onChange={(e) => handleInputChange('emergencyContact.phone', e.target.value)}
                  className={errors['emergencyContact.phone'] ? 'error' : ''}
                />
                {errors['emergencyContact.phone'] && <span className="error-text">{errors['emergencyContact.phone']}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Address Line 1</label>
                <input
                  type="text"
                  value={formData.emergencyContact.address.line1}
                  onChange={(e) => handleInputChange('emergencyContact.address.line1', e.target.value)}
                />
              </div>
              
              <div className="form-group full-width">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={formData.emergencyContact.address.line2}
                  onChange={(e) => handleInputChange('emergencyContact.address.line2', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.emergencyContact.address.city}
                  onChange={(e) => handleInputChange('emergencyContact.address.city', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.emergencyContact.address.state}
                  onChange={(e) => handleInputChange('emergencyContact.address.state', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.emergencyContact.address.zip}
                  onChange={(e) => handleInputChange('emergencyContact.address.zip', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>Country</label>
                <select
                  value={formData.emergencyContact.address.country}
                  onChange={(e) => handleInputChange('emergencyContact.address.country', e.target.value)}
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="step-content">
            <h3 className="step-title">Church Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Church Name *</label>
                <ChurchNameInput
                  value={formData.church.name}
                  onChange={(value) => handleInputChange('church.name', value)}
                  className={errors['church.name'] ? 'error' : ''}
                  error={errors['church.name']}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Pastor First Name *</label>
                <input
                  type="text"
                  value={formData.church.pastorFirstName}
                  onChange={(e) => handleInputChange('church.pastorFirstName', e.target.value)}
                  className={errors['church.pastorFirstName'] ? 'error' : ''}
                />
                {errors['church.pastorFirstName'] && <span className="error-text">{errors['church.pastorFirstName']}</span>}
              </div>
              
              <div className="form-group">
                <label>Pastor Last Name *</label>
                <input
                  type="text"
                  value={formData.church.pastorLastName}
                  onChange={(e) => handleInputChange('church.pastorLastName', e.target.value)}
                  className={errors['church.pastorLastName'] ? 'error' : ''}
                />
                {errors['church.pastorLastName'] && <span className="error-text">{errors['church.pastorLastName']}</span>}
              </div>
              
              <div className="form-group">
                <label>Church Phone *</label>
                <input
                  type="tel"
                  value={formData.church.phone}
                  onChange={(e) => handleInputChange('church.phone', e.target.value)}
                  className={errors['church.phone'] ? 'error' : ''}
                />
                {errors['church.phone'] && <span className="error-text">{errors['church.phone']}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Church Address Line 1</label>
                <input
                  type="text"
                  value={formData.church.address.line1}
                  onChange={(e) => handleInputChange('church.address.line1', e.target.value)}
                />
              </div>
              
              <div className="form-group full-width">
                <label>Church Address Line 2</label>
                <input
                  type="text"
                  value={formData.church.address.line2}
                  onChange={(e) => handleInputChange('church.address.line2', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.church.address.city}
                  onChange={(e) => handleInputChange('church.address.city', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.church.address.state}
                  onChange={(e) => handleInputChange('church.address.state', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.church.address.zip}
                  onChange={(e) => handleInputChange('church.address.zip', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>Country</label>
                <select
                  value={formData.church.address.country}
                  onChange={(e) => handleInputChange('church.address.country', e.target.value)}
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h3 className="step-title">Faith Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Are you a born again believer? *</label>
                <select
                  value={formData.faith.bornAgain}
                  onChange={(e) => handleInputChange('faith.bornAgain', e.target.value)}
                  className={errors['faith.bornAgain'] ? 'error' : ''}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {errors['faith.bornAgain'] && <span className="error-text">{errors['faith.bornAgain']}</span>}
              </div>
              
              <div className="form-group">
                <label>How long have you been a believer? *</label>
                <input
                  type="text"
                  value={formData.faith.believerLength}
                  onChange={(e) => handleInputChange('faith.believerLength', e.target.value)}
                  className={errors['faith.believerLength'] ? 'error' : ''}
                  placeholder="e.g., 5 years, 10+ years, etc."
                />
                {errors['faith.believerLength'] && <span className="error-text">{errors['faith.believerLength']}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Personal Testimony *</label>
                <textarea
                  value={formData.faith.testimony}
                  onChange={(e) => handleInputChange('faith.testimony', e.target.value)}
                  className={errors['faith.testimony'] ? 'error' : ''}
                  rows={4}
                  placeholder="Please share your personal testimony of how you came to know Jesus Christ as your Lord and Savior..."
                />
                {errors['faith.testimony'] && <span className="error-text">{errors['faith.testimony']}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Personal Evangelism Experience</label>
                <textarea
                  value={formData.faith.evangelismExperience}
                  onChange={(e) => handleInputChange('faith.evangelismExperience', e.target.value)}
                  rows={3}
                  placeholder="Describe your experience with personal evangelism, if any..."
                />
              </div>
              
              <div className="form-group">
                <label>Currently practicing personal evangelism?</label>
                <select
                  value={formData.faith.currentlyPracticing}
                  onChange={(e) => handleInputChange('faith.currentlyPracticing', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Have you been on a mission trip before?</label>
                <select
                  value={formData.faith.previousMissionTrips}
                  onChange={(e) => handleInputChange('faith.previousMissionTrips', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>Mission Trip Experience</label>
                <textarea
                  value={formData.faith.missionTripExperience}
                  onChange={(e) => handleInputChange('faith.missionTripExperience', e.target.value)}
                  rows={3}
                  placeholder="Describe your mission trip experience, if any..."
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="step-content">
            <h3 className="step-title">Event Questions</h3>
            <div className="form-grid">
              {eventCustomFields.length > 0 ? (
                eventCustomFields.map((field) => (
                  <div
                    key={field.id}
                    className={`form-group ${
                      field.field_type === 'textarea' ? 'full-width' : ''
                    }`}
                  >
                    <DynamicFormField
                      field={field}
                      value={customFieldResponses[field.field_key]}
                      onChange={(value) => handleCustomFieldChange(field.field_key, value)}
                      error={errors[`customField.${field.field_key}`]}
                      disabled={isSubmitting}
                      isPreFilled={savedCustomFieldKeys.has(field.field_key)}
                    />
                  </div>
                ))
              ) : (
                <p className="text-muted">No additional questions for this event.</p>
              )}

              <div className="form-group full-width" style={{ marginTop: '1rem' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.optedIn}
                    onChange={(e) => handleInputChange('optedIn', e.target.checked)}
                  />
                  I agree to receive communications about this event and future OBP events
                </label>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  // Show confirmation page if application was submitted
  if (isSubmitted) {
    return (
      <ApplicationConfirmation
        applicationId={submittedApplicationId || undefined}
        eventTitle={eventTitle}
        applicantName={`${formData.firstName} ${formData.lastName}`}
        applicantEmail={formData.email}
        submissionDate={new Date().toISOString()}
        isNewAccount={isNewAccount}
        coordinatorContact={coordinatorContact ?? undefined}
      />
    );
  }

  // Show login prompt if email already exists
  if (showLoginPrompt) {
    return (
      <div className="mission-application-form">
        <div className="login-prompt-container">
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Account Already Exists</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              An account with the email <strong>{formData.email}</strong> already exists.
              Please sign in to continue with your application.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              <a
                href={`/login?redirect=${encodeURIComponent(signInRedirectPath)}`}
                className="btn btn-primary"
                style={{ textAlign: 'center', padding: '0.75rem 1.5rem' }}
              >
                <i className="fas fa-sign-in-alt me-2" aria-hidden></i>
                Sign In to Continue
              </a>
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  setCurrentStep(1);
                }}
                className="btn btn-outline"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                <i className="fas fa-envelope me-2" aria-hidden></i>
                Use a Different Email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show already-applied message for duplicate anonymous submissions to the same event
  if (showAlreadyApplied) {
    return (
      <div className="mission-application-form">
        <div className="login-prompt-container">
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Application Already Submitted</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              An application for <strong>{formData.email}</strong> has already been submitted for this event.
              If you need to make changes, please contact the event coordinator.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              <button
                onClick={() => {
                  setShowAlreadyApplied(false);
                  setCurrentStep(1);
                }}
                className="btn btn-outline"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                <i className="fas fa-envelope me-2" aria-hidden></i>
                Use a Different Email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Build selected options summary for display under title
  const selectedOptionsLabel = signupOptions
    .filter((opt) => selectedSignupOptions.includes(opt.id))
    .map((opt) => opt.label)
    .join(', ');

  const selectedOptionsTotal = signupOptions
    .filter((opt) => selectedSignupOptions.includes(opt.id))
    .reduce((sum, opt) => sum + Number(opt.price), 0);
  const showSelectedOptionsTotal = hasSignupPrice(selectedOptionsTotal);

  return (
    <div className="mission-application-form">
      <div className="form-header">
        <h2>{eventTitle} - Application Form</h2>
        {selectedOptionsLabel && (
          <p className="selected-registration" style={{ color: '#16A34A', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
            <i className="fas fa-check-circle me-1"></i>
            {selectedOptionsLabel}
            {showSelectedOptionsTotal && (
              <> - {formatSignupPrice(selectedOptionsTotal)}</>
            )}
          </p>
        )}
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }}></div>
        </div>
        <p className="step-indicator">Step {currentStep} of {totalSteps}</p>
      </div>

      {/* Error banner for save errors */}
      {saveError && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ marginBottom: '1.5rem' }}>
          <strong>Error:</strong> {saveError}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSaveError(null)}
          ></button>
        </div>
      )}

      {/* Sign in prompt for guests */}
      {!user && (
        <div className="sign-in-prompt" style={{
          background: '#F0FDF4',
          border: '1px solid #BBF7D0',
          borderRadius: '8px',
          padding: '1rem 1.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <span style={{ color: '#166534' }}>
            Already have an account? Sign in to use your saved information.
          </span>
          <a
            href={`/login?redirect=${encodeURIComponent(signInRedirectPath)}`}
            className="btn btn-sm"
            style={{
              background: '#16A34A',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
          >
            <i className="fas fa-sign-in-alt me-1" aria-hidden></i>
            Sign In
          </a>
        </div>
      )}

      {/* Registration Options Selection */}
      {signupOptions.length > 0 && (
        <div className="card mb-4" style={{ border: errors.signupOptions ? '2px solid #dc3545' : undefined }}>
          <div className="card-body">
            <h5 className="mb-3">
              {signupOptionMode === 'single' ? 'Select Registration Option *' : 'Select Registration Options *'}
            </h5>
            <div className="row">
              {signupOptions
                .sort((a, b) => a.display_order - b.display_order)
                .map((option) => {
                  const isSelected = selectedSignupOptions.includes(option.id);
                  const optionDates = formatSignupOptionSchedule(option);

                  return (
                    <div key={option.id} className="col-md-6 mb-3">
                      <div
                        className={`border rounded p-3 h-100 ${isSelected ? 'border-primary bg-light' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSignupOptionToggle(option.id)}
                      >
                        <div className="form-check">
                          <input
                            type={signupOptionMode === 'single' ? 'radio' : 'checkbox'}
                            className="form-check-input"
                            name="signupOption"
                            checked={isSelected}
                            onChange={() => handleSignupOptionToggle(option.id)}
                          />
                          <label className="form-check-label" style={{ cursor: 'pointer' }}>
                            <strong>{option.label}</strong>
                          </label>
                        </div>
                        {optionDates && (
                          <p className="mb-1 small text-muted">{optionDates}</p>
                        )}
                        {hasSignupPrice(option.price) && (
                          <p className="fs-5 fw-bold text-primary mb-0">
                            {formatSignupPrice(option.price)}
                          </p>
                        )}
                        {option.description && (
                          <p className="mb-0 small text-muted mt-2">{option.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            {signupOptionMode === 'multi' &&
              selectedSignupOptions.length > 0 &&
              showSelectedOptionsTotal && (
              <div className="mt-2 text-end">
                <strong>Total: {formatSignupPrice(selectedOptionsTotal)}</strong>
              </div>
            )}
            {errors.signupOptions && (
              <div className="text-danger mt-2">
                <small>{errors.signupOptions}</small>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-container">
        {renderStepContent()}
        
        <div className="form-actions">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              <i className="fas fa-arrow-left me-2" aria-hidden></i>
              Previous
            </button>
          )}
          
          <div className="right-actions">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="btn btn-outline"
              disabled={isSubmitting}
            >
              {!isSubmitting && <i className="fas fa-save me-2" aria-hidden></i>}
              {isSubmitting ? 'Saving...' : 'Save Draft'}
            </button>
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                Next
                <i className="fas fa-arrow-right ms-2" aria-hidden></i>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {!isSubmitting && <i className="fas fa-paper-plane me-2" aria-hidden></i>}
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionApplicationForm;
